import { useState, useEffect, useMemo, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DataTable from "cxfcomponents/Tables/DataTable";
import { useAuth0 } from "@auth0/auth0-react";
import StatusBadge from "cxfcomponents/StatusBadge";
import { UserContext } from "context/AppProvider/UserContext";
import { PermissionsContext } from "context/AppProvider/PermissionsContext";
import UsersModalUnshare from "../UsersModal/index3";
import { switchView } from "redux/actions/portal/flowsActionCreator.js";
import { getLocalDateTimeString, formatDateAndTime } from "helpers/common";
import { usePortalFlows } from "hooks/usePortalFlows";
import { useUserFlows } from "hooks/useUserFlows";
import TileView from "../TileView";
import { useRoles } from "hooks/useRoles";
import defaultFlowIcon from "assets/images/for-flow/flow-icon-list-view.svg";
import TextWithEllipsis from "layouts/pages/logs/components/TextWithEllipsis";
import { PERMISSIONS } from "constants/portal";
import SharerFlowNotificationDialog from "../SharerFlowNotificationDialog";
import { useNavigate } from "react-router-dom";
import UsersCell from "../UsersCell";
import FlowMenu from "../FlowMenu";
import UsersModal2 from "../UsersModal/index2";
import ImportFlowDialog from "../ImportFlowDialog";
import { FlowContext } from "layouts/Flows/context/FlowContext";
import Tooltip from "@mui/material/Tooltip";
import useCloneFlow from "hooks/useCloneFlow";
import { useIsOwner } from "hooks/useIsOwner";
import { permissionChecker } from "helpers/common";
import { setSavedFlow } from "redux/actions/flowDesigner/flowsActionCreator";
import { fetchFlowById } from "api/FlowDesignerApis";
import { useResourceListener } from "hooks/useResourceListener";

const options = {
	status: [
		{ value: "active", label: "Active" },
		{ value: "test", label: "Draft" },
		{ value: "deleted", label: "Deleted" }
	]
};

export const FlowsTable = ({ tableHeight }) => {
	const [titleName, setTitleName] = useState("Share");
	const { user } = useAuth0();
	const { setUserData } = useContext(UserContext);
	const { userPermissions } = useContext(PermissionsContext);
	const [openUsersModal, setOpenUsersModal] = useState(false);
	const [openUsersModalUnshare, setOpenUsersModalUnshare] = useState(false);
	const [selectedUserFlow, setSelectedUserFlow] = useState(null);
	const [sharedFlows, setSharedFlows] = useState(null);
	const [loadDatatable, setLoadDatatable] = useState(false);
	const [shareList, setShareList] = useState(null);
	const [unshareList, setUnshareList] = useState(null);
	const [openImportFlowDialog, setOpenImportFlowDialog] = useState(false);
	const { flowView } = useSelector((store) => store?.portal?.flows);
	const dispatch = useDispatch();
	const { users } = useSelector((store) => store?.portal?.users);
	const { roles } = useRoles(false);
	const [tableView, setTableView] = useState("list");
	const { flows, handleFetchFlows } = usePortalFlows();
	const { userFlows, handleFetchUserFlows } = useUserFlows();
	const { userData } = useContext(UserContext);
	const { reloadFlowsCount } = useSelector((store) => store?.designer?.flows);
	const navigate = useNavigate();
	const { handleCloneFlow } = useCloneFlow();
	const isOwner = useIsOwner();
	useResourceListener(
		"flowsSharingUpdate",
		[reloadFlows, handleFetchFlows],
		250
	);

	const handleOpenImportFlowDialog = () => {
		setOpenImportFlowDialog(true);
	};

	const handleCloseImportFlowDialog = () => {
		setOpenImportFlowDialog(false);
	};

	const handleFlowView = (view) => {
		setTableView(view);
		localStorage.setItem("flowView", view);
	};

	const handleImportFlow = async (isSuccess = true) => {
		if (isSuccess) {
			handleFetchFlows();
			handleCloseImportFlowDialog();
		}
	};

	async function reloadFlows() {
		handleFetchFlows();
		handleFetchUserFlows();
	}

	const handleOpenFlowDetail = async (id) => {
		if (window.location.href.includes("designer")) {
			const flow = await fetchFlowById(user?.stripeId, id);
			dispatch(setSavedFlow(flow));

			navigate(`/designer/${id}`, { replace: true });
			return;
		}

		navigate(`/flow/${id}`);
	};

	const getStatus = (flow) => {
		if (flow.trashed) return "deleted";

		if (flow.shared_status === "test") return "test";

		if (flow.steps === "") return "draft";

		return "active";
	};

	const handleFilterFlow = (flow) => {
		if (
			isOwner ||
			permissionChecker(userPermissions, PERMISSIONS.VIEW_ANY_WORKFLOWS)
		) {
			return true;
		}

		const foundFlow = userFlows.find(
			({ flow_id, user_id }) =>
				flow_id === flow.id && user_id === user.cxfUserId
		);

		if (foundFlow || flow.created_by_id === user.cxfUserId) return true;

		return false;
	};

	const dataTableData = useMemo(() => {
		return {
			columns: [
				{
					Header: "flowId",
					accessor: "flowId",
					show: false
				},
				{
					Header: "icon",
					accessor: "icon",
					canGroupBy: false,
					disableSortBy: true,
					Cell: ({ value, row }) => {
						const flowItem = row.original.item;

						return (
							<SoftBox width="60px">
								<SoftBox
									component="img"
									width="100%"
									minHeight="60px"
									maxHeight="60px"
									border="0.5px solid #E6E6E6"
									borderRadius="12px"
									sx={{
										objectFit: "cover"
									}}
									src={`${value}?v=${flowItem?.logoUpdatedAt ?? flowItem?.updated_date_time ?? ""}`}
								/>
							</SoftBox>
						);
					}
				},
				{
					Header: "name",
					accessor: "nameWithBadge",
					width: 250,
					canGroupBy: false,
					Cell: ({ value }) => (
						<Tooltip
							title={value?.name || ""}
							arrow
						>
							<SoftBox
								className="white-to-dark"
								fontSize="14px"
								fontWeight="bold"
								display="flex"
								alignItems="center"
								sx={{
									minWidth: "150px",
									maxWidth: "200px",
									whiteSpace: "normal",
									cursor: "pointer",
									wordBreak: "break-all"
								}}
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									e.defaultMuiPrevented = true;
									handleOpenFlowDetail(value?.id);
								}}
							>
								{value?.name?.length > 24
									? value?.name?.slice(0, 24) + "..."
									: value?.name}
							</SoftBox>
						</Tooltip>
					),
					sortType: (rowA, rowB, columnId) => {
						const nameA = rowA.values[columnId].name;
						const nameB = rowB.values[columnId].name;
						return nameA.localeCompare(nameB);
					}
				},
				{
					Header: "description",
					accessor: "description",
					canGroupBy: false,
					Cell: ({ value }) => {
						return (
							<SoftBox>
								<TextWithEllipsis
									value={{
										name: value,
										maxWidth: "clamp(100px, 20vw, 300px)"
									}}
								/>
							</SoftBox>
						);
					}
				},
				{
					Header: "name",
					accessor: "name",
					canGroupBy: false,
					show: false
				},
				{
					Header: "status",
					accessor: "status",
					canGroupBy: false,
					Cell: ({ value }) => {
						return <StatusBadge status={value} />;
					}
				},
				{
					Header: "shared status",
					accessor: "sharedStatus",
					canGroupBy: true,
					expanded: true
				},
				{
					Header: "users",
					accessor: "users",
					canGroupBy: false,
					Cell: ({ value }) => {
						return <UsersCell users={value} />;
					},
					disableSortBy: true
				},
				{
					Header: "last edited",
					accessor: "lastEdited",
					width: 170,
					canGroupBy: false,
					disableSortBy: false,
					Cell: ({ value }) => <SoftTypography fontSize="14px">{value}</SoftTypography>,
					defaultSortOrder: "desc",
					sortType: (rowA, rowB, columnId) => {
						const dateA = new Date(rowA.values[columnId]);
						const dateB = new Date(rowB.values[columnId]);
						return dateA - dateB;
					}
				},
				{
					Header: "actions",
					accessor: "action",
					canGroupBy: false,
					disableSortBy: true
				}
			].map((column) => ({ ...column, disableResize: true })),
			rows:
				flows?.filter(handleFilterFlow).map((item) => {
					const OwnerFromFlow = item.created_by_id;

					const OwnerUser =
						users?.filter((owner) =>
							OwnerFromFlow?.includes(owner.auth0_id)
						) || [];

					const getUsersFromUF = userFlows?.filter(
						(uf) => uf.flow_id === item.id
					);

					const sharedToUsers = getUsersFromUF?.map((userid) => {
						return userid.user_id;
					});

					const sharedUsers =
						users?.filter((sharedTo) =>
							sharedToUsers.includes(sharedTo.auth0_id)
						) || [];

					const mergedArrayforOwner = [...OwnerUser, ...sharedUsers];

					const foundSharedFlow = userFlows.find(
						(userFlow) =>
							userFlow.user_id === user.cxfUserId &&
							userFlow.flow_id === item.id
					);

					return {
						flowId: item.id,
						nameWithBadge: {
							trashed: item.trashed,
							name: item.name,
							id: item.id
						},
						name: item.name,
						sharedStatus:
							item.created_by_id === user?.cxfUserId
								? "Owner"
								: foundSharedFlow
									? "Shared"
									: "",
						status: `${getStatus(item)}`,
						users: mergedArrayforOwner,
						lastEdited:
							formatDateAndTime(
								getLocalDateTimeString(
									item.updated_date_time || item.created_date_time
								)
							) || "",
						action: (
							<FlowMenu
								item={{ ...item, userAccessType: item.userAccessType }}
								flows={flows}
								getFlows={handleFetchFlows}
								userFlows={userFlows}
								setOpenUsersModal={setOpenUsersModal}
								setOpenUsersModalUnshare={setOpenUsersModalUnshare}
								setSelectedUserFlow={setSelectedUserFlow}
								forOwner
								titleName={titleName}
								setTitleName={setTitleName}
								users={users || []}
								userPermissions={userPermissions}
								handleCloneFlow={handleCloneFlow}
							/>
						),
						description: item?.description,
						icon: item.logo ? item.logo : defaultFlowIcon,
						item
					};
				}) ?? []
		};
	}, [
		flows,
		userFlows,
		sharedFlows,
		users,
		isOwner,
		userData,
		userPermissions
	]);

	useEffect(() => {
		if (users) {
			setUserData(users.find((item) => item.auth0_id === user?.cxfUserId));
		}
	}, [users]);

	useEffect(() => {
		if (userFlows && flows) {
			const filteredFlows = [];
			const sharedUserFlows = userFlows.filter(
				(item) => item.user_id === user?.cxfUserId
			);

			sharedUserFlows.forEach((sharedFlow) => {
				const flowId = sharedFlow.flow_id;
				const flow = flows.find((flow) => flow.id === flowId);

				if (flow) {
					filteredFlows.push({
						...flow,
						userAccessType: sharedFlow.access_type
					});
				}
			});
			setSharedFlows(filteredFlows);
		}
	}, [userFlows, flows]);

	useEffect(() => {
		// TODO - change to user preferences
	}, [flowView]);

	useEffect(() => {
		setTableView(localStorage.getItem("flowView") ?? "list");
	}, []);

	useEffect(() => {
		if (reloadFlowsCount > 0) {
			reloadFlows();
		}
	}, [reloadFlowsCount]);

	return (
		<FlowContext.Provider value={{ handleOpenImportFlowDialog }}>
			<DataTable
				table={dataTableData}
				entriesPerPage={{ defaultValue: 25 }}
				options={options}
				hiddenTopButtons={false}
				reloadTableData={reloadFlows}
				handleOpenImportFlowDialog={handleOpenImportFlowDialog}
				buttonPermissions={userPermissions ?? []}
				setFlowView={(payload) => dispatch(switchView(payload))}
				loadDatatable={loadDatatable}
				setLoadDatatable={setLoadDatatable}
				splitted={true}
				tableView={tableView}
				tileViewComponent={(rows) => (
					<TileView
						data={rows}
						groupBy={[]}
						onFlowClick={handleOpenFlowDetail}
					/>
				)}
				setTableView={handleFlowView}
				withTileview={true}
				withFlowsOptions={true}
				withStatusFilter={true}
				withSearchbar={true}
				tableHeight={tableHeight}
			/>
			<UsersModal2
				users={users || []}
				roles={roles}
				userFlows={userFlows}
				selectedUserFlow={selectedUserFlow}
				openUsersModal={openUsersModal}
				setOpenUsersModal={setOpenUsersModal}
				titleName={titleName}
				shareList={shareList}
				flowView={1}
				setShareList={setShareList}
			/>
			<UsersModalUnshare
				users={users || []}
				roles={roles}
				userFlows={userFlows}
				selectedUserFlow={selectedUserFlow}
				openUsersModalUnshare={openUsersModalUnshare}
				setOpenUsersModalUnshare={setOpenUsersModalUnshare}
				titleName={titleName}
				unshareList={unshareList}
				flowView={1}
				setUnshareList={setUnshareList}
			/>
			<ImportFlowDialog
				open={openImportFlowDialog}
				onClose={handleCloseImportFlowDialog}
				onAccept={handleImportFlow}
				dialogTitle={"Import flow"}
				acceptButtonText={"import"}
				acceptButtonColor={"info"}
			/>
			<SharerFlowNotificationDialog />
		</FlowContext.Provider>
	);
};
